import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Lightbulb, Target, Users, Zap } from "lucide-react";
import { RelatedServices } from "@/components/RelatedServices";
import { Cloud, Server, Database } from "lucide-react";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function Consulting() {
  const { openDrawer } = useQuoteDrawer();

  const services = [
    {
      title: "Technology Assessment",
      description: "Comprehensive evaluation of your current technology stack and infrastructure.",
      icon: <Target className="h-8 w-8" />
    },
    {
      title: "Digital Strategy Planning",
      description: "Strategic roadmaps for digital transformation and business growth.",
      icon: <Lightbulb className="h-8 w-8" />
    },
    {
      title: "Architecture Consulting",
      description: "Design scalable and robust system architectures for your projects.",
      icon: <Zap className="h-8 w-8" />
    },
    {
      title: "Team Training",
      description: "Upskill your team with the latest technologies and best practices.",
      icon: <Users className="h-8 w-8" />
    }
  ];

  return (
    <>
      <SEO 
        title="IT Consulting Services UAE | Kecly"
        description="Expert IT consulting in Dubai & Sharjah. Digital transformation, cloud migration and technology strategy for businesses."
        keywords="it consulting uae, digital transformation dubai, tech strategy sharjah"
      />

      <main>
   

        {/* Hero Section */}
        <section className="relative py-32 bg-gradient-to-br from-blue-50 to-gray-50 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            {/* Animated consulting elements */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              {[...Array(15)].map((_, i) => (
                <div 
                  key={i}
                  className="absolute rounded-full bg-purple-200"
                  style={{
                    width: `${Math.random() * 10 + 5}px`,
                    height: `${Math.random() * 10 + 5}px`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animation: `pulse ${Math.random() * 5 + 3}s infinite alternate`
                  }}
                />
              ))}
            </div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center lg:text-left"
              >
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
                    Business Consulting
                  </span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-lg">
                  Expert guidance to transform your business with strategic planning and actionable insights.
                </p>
                
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-12">
                  {['Strategy', 'Planning', 'Growth', 'Optimization'].map((service) => (
                    <span key={service} className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-700 border border-gray-200">
                      {service}
                    </span>
                  ))}
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-medium text-white shadow-lg"
                  onClick={() => openDrawer()}
                >
                  Schedule Consultation
                </motion.button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                {/* Glass morphism card */}
                <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 backdrop-blur-md border border-gray-700/50 rounded-2xl p-8 shadow-2xl overflow-hidden">
                  <div className="absolute -right-20 -top-20 w-64 h-64 bg-blue-500/5 rounded-full filter blur-xl"></div>
                  <div className="flex justify-center mb-8">
                    <Users className="h-20 w-20 text-white" />
                  </div>
                  
                  {/* Consultation process */}
                  <div className="space-y-4 mb-6">
                    {[
                      { step: '1', title: 'Discovery', desc: 'Understand your business goals' },
                      { step: '2', title: 'Analysis', desc: 'Evaluate current operations' },
                      { step: '3', title: 'Strategy', desc: 'Develop customized solutions' },
                      { step: '4', title: 'Implementation', desc: 'Execute the action plan' }
                    ].map((item) => (
                      <div key={item.step} className="flex items-start">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-500/20 flex items-center justify-center mr-3 mt-1">
                          <span className="text-white text-sm font-medium">{item.step}</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-white">{item.title}</h3>
                          <p className="text-gray">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Industry icons */}
                  <div className="flex justify-center space-x-4">
                    {['Tech', 'Finance', 'Retail', 'Healthcare'].map((industry) => (
                      <div key={industry} className="p-2 bg-gray-800 rounded-full">
                        <div className="h-6 w-6 bg-gradient-to-br from-purple-400 to-blue-500 rounded-full"></div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Floating expertise badges */}
                <div className="absolute -bottom-5 -right-5 flex space-x-2">
                  {['Strategy', 'Planning', 'Growth', 'ROI'].map((expertise) => (
                    <span key={expertise} className="px-2 py-1 text-xs font-medium rounded-full bg-gray-800/80 text-gray-300 backdrop-blur-sm">
                      {expertise}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Our Consulting Services</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive solutions to address your business challenges
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 p-8 rounded-xl shadow-sm border border-gray-200"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6 text-blue-600">
                    {service.icon}
                  </div>
                  <h3 className="font-orbitron font-semibold text-xl mb-4 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="Consulting" 
              services={[
                {
                  title: "Cloud Services",
                  description: "Scalable, secure, and high-performance cloud solutions for your business.",
                  icon: <Cloud className="h-8 w-8" />
                },
                {
                  title: "Data Analytics",
                  description: "Transform raw data into actionable business insights.",
                  icon: <Database className="h-8 w-8" />
                },
                {
                  title: "Digital Marketing",
                  description: "Strategic digital marketing solutions to grow your business.",
                  icon: <Lightbulb className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Our Consulting Process</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Systematic approach to deliver actionable insights and recommendations
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Current State Analysis", description: "Assess existing systems and processes" },
                { step: "02", title: "Strategy Development", description: "Create tailored technology roadmap" },
                { step: "03", title: "Implementation Planning", description: "Detailed execution plan with timelines" },
                { step: "04", title: "Execution Support", description: "Ongoing guidance during implementation" }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="font-orbitron font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-2 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Web Design Locations */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <WebDesignLocations />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-50">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Get expert guidance to navigate your digital transformation journey
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => openDrawer()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg"
                >
                  Book Free Consultation
                </Button>
                <Link href="/contact">
                  <Button 
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg"
                  >
                    Discuss Your Challenges
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
